import { supportsRuntimeVersion } from '@mulesoft/monitoring-center-components/lib/components/SupportedRuntimesHelper/SupportedRuntimesHelper'
import { ResourceType } from '../../common/resource/model';
import { commonDashboardRegistry } from '@mulesoft/monitoring-center-components/lib/components/BuiltInDashboards/PreBuilt';
import { DashboardItem } from
        '@mulesoft/monitoring-center-components/lib/components/BuiltInDashboards/BuiltInDashboardsContent/BuiltInDashboardsContent';
import { MenuItemValue } from '@mulesoft/monitoring-center-components/lib/components/Menu/MenuItemValue';
import FeatureFlagService from '../../services/FeatureFlagService';
import FeatureFlags from '../../types/featureflags';

// The keys of the dashboardRegistry object are internal identifiers and not to be confused with the `key` attribute in the values.
// The values represent metadata related to a dashboard. It has another field called `key` that is used everywhere to identify the dashboard.
// The `key` attribute from the values is intentionally not used as a key for this registry to
//  safeguard against potential renames and messing up urls where the key might appear.
export const dashboardRegistry: { [key: string]: MenuItemValue } = {
    ...commonDashboardRegistry,
    applicationNetwork: {
        key: 'application-network', // referred to as dashboardType. Also used for constructing urls.
        label: 'Application Network',
        icon: 'dashboard'
    },
    customMetrics: {
        key: 'custom-metrics',
        label: 'Custom metrics',
        icon: 'manage'
    },
    requests: {
        key: 'requests',
        label: 'Requests',
        icon: 'sign-in'
    },
    policies: {
        key: 'policies',
        label: 'Policies',
        icon: 'encrypted-small'
    },
    clients: {
        key: 'clients',
        label: 'Client applications',
        icon: 'user-small'
    }
};

const titaniumDashboards = [dashboardRegistry.connectors,
    dashboardRegistry.customMetrics,
    dashboardRegistry.flows,
    dashboardRegistry.applicationNetwork];

const allDashboards = [dashboardRegistry.overview,
    dashboardRegistry.inbound,
    dashboardRegistry.outbound,
    dashboardRegistry.flows,
    dashboardRegistry.connectors,
    dashboardRegistry.performance,
    dashboardRegistry.failures,
    dashboardRegistry.jvm,
    dashboardRegistry.infrastructure,
    dashboardRegistry.customMetrics,
    dashboardRegistry.applicationNetwork];

const apiAnalyticsDashboards = [dashboardRegistry.httpAnalytics];
const apiMetricsDashboards = [dashboardRegistry.overview,
    dashboardRegistry.requests,
    dashboardRegistry.policies,
    dashboardRegistry.failures,
    dashboardRegistry.performance,
    dashboardRegistry.clients];
const apiDashboards = apiAnalyticsDashboards.concat(apiMetricsDashboards);

const targetDashboards = [dashboardRegistry.jvm, dashboardRegistry.infrastructure, dashboardRegistry.applications]

const supportedDashboardsForResourceType = {
    [ResourceType.APP]: allDashboards,
    [ResourceType.MC]: allDashboards,
    [ResourceType.HYBRID]: allDashboards,
    [ResourceType.SERVER]: targetDashboards,
    [ResourceType.SERVER_GROUP]: targetDashboards,
    [ResourceType.CLUSTER]: targetDashboards,
    [ResourceType.API]: apiDashboards,
    [ResourceType.MANAGED_API]: apiMetricsDashboards
};

const getSupportedDashboards = (isPremium: boolean, resourceType: ResourceType, muleVersion?: string, apiMetricsCompatible?: boolean) => {

    const isApiAnalyticsBuiltInDashboardDisabled = FeatureFlagService.get(FeatureFlags.API_ANALYTICS_BUILT_IN_DASHBOARD_DISABLED)

    // If API_ANALYTICS_BUILT_IN_DASHBOARD_DISABLED feature flag is true, we must not show the metrics from the api-analytics dashboard.
    if (resourceType === ResourceType.API && isApiAnalyticsBuiltInDashboardDisabled ) {
        return apiMetricsDashboards
    }
    if (resourceType === ResourceType.API) {
        return apiMetricsCompatible ? apiMetricsDashboards : apiAnalyticsDashboards
    }
    if (!isPremium || (resourceType === ResourceType.APP && !supportsRuntimeVersion(muleVersion))) {
        return supportedDashboardsForResourceType[resourceType]
            .filter((dashboard: MenuItemValue) => (titaniumDashboards.map(d => d.key).indexOf(dashboard.key) === -1))
    }
    return supportedDashboardsForResourceType[resourceType]
};

export const getLabel = (dashboardType: string): string | null => {
    for (const registryKey of Object.keys(dashboardRegistry)) {
        let item = dashboardRegistry[registryKey];
        if (item.key === dashboardType) {
            return item.label || null
        }
    }
    return null;
};

export const getDashboardType = (isPremium: boolean, resourceType: ResourceType,
                                 dashboardType?: string, muleVersion?: string, apiMetricsCompatible?: boolean): string => {
    const supportedDashboards = getSupportedDashboards(isPremium, resourceType, muleVersion, apiMetricsCompatible);
    const maybeMatch = supportedDashboards.find((dashboard: MenuItemValue) => dashboard.key === dashboardType);
    return maybeMatch ? maybeMatch.key : supportedDashboards[0].key;
};

export const getFirstApiAnalyticsDashboardType = (): string => apiAnalyticsDashboards[0].key;

export const getFirstApiMetricDashboardType = (): string => apiMetricsDashboards[0].key;

export const isDashboardNotSupportedForAPIResourceType = (dashboardType: string): boolean => apiDashboards.map(d => d.key).indexOf(dashboardType) < 0;

export const isDashboardNotSupportedForApiMetricTypes = (urlDashboardType: string): boolean =>
    apiMetricsDashboards.map(d => d.key).indexOf(urlDashboardType) === -1;

export const isDashboardNotSupportedForApiAnalyticsTypes = (urlDashboardType: string): boolean =>
    apiAnalyticsDashboards.map(d => d.key).indexOf(urlDashboardType) === -1;

export const selectFree = (dashboards: DashboardItem[]): DashboardItem[] =>
    dashboards.filter(dashboard => (titaniumDashboards.map(d => d.key).indexOf(dashboard.item.key) === -1));
